import { api } from './api'

/**
 * Web Push enrolment, browser side.
 *
 * The iOS rules this has to respect, none of which are guesses — they are why
 * the flow looks the way it does:
 *
 *  - Push only works in a PWA installed to the Home Screen. In Safari proper,
 *    `Notification` is undefined and there is nothing to ask for.
 *  - `requestPermission()` must be called synchronously from a user gesture.
 *    Prompting on page load is denied outright, and a denial is sticky — the
 *    only way back is deleting and re-installing the app, so a mistimed prompt
 *    is expensive.
 *  - A subscription is bound to its origin. Re-installing from a different
 *    origin means subscribing again from scratch.
 */

export type PushSupport =
  | { supported: true }
  | { supported: false; reason: 'unsupported' | 'not-installed' }

/**
 * `standalone` is how a Home-Screen install identifies itself. Safari's tab is
 * excluded on purpose: the API exists there on iOS but subscribing always
 * fails, and offering a button that cannot work is worse than hiding it.
 */
export function detectSupport(): PushSupport {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { supported: false, reason: 'unsupported' }
  }

  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true

  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent)
  if (isIos && !standalone) return { supported: false, reason: 'not-installed' }

  return { supported: true }
}

export function permissionState(): NotificationPermission | 'unavailable' {
  if (typeof Notification === 'undefined') return 'unavailable'
  return Notification.permission
}

/** base64url → the Uint8Array `pushManager.subscribe` insists on. */
function decodeKey(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register('/sw.js', { scope: '/' })
}

/**
 * Must be called directly from a click handler — see the gesture rule above.
 * Returns false when the user declines, which is terminal on iOS.
 */
export async function enablePush(): Promise<boolean> {
  const { configured, publicKey } = await api<{ configured: boolean; publicKey?: string }>(
    '/push/key',
  )
  if (!configured || !publicKey) return false

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return false

  const registration = await registerServiceWorker()
  await navigator.serviceWorker.ready

  // An existing subscription is reused rather than replaced: re-subscribing
  // with a different key silently breaks delivery to the old endpoint.
  const existing = await registration.pushManager.getSubscription()
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      // Non-negotiable on every browser that matters: a push must be shown.
      userVisibleOnly: true,
      applicationServerKey: decodeKey(publicKey) as BufferSource,
    }))

  const json = subscription.toJSON() as { endpoint?: string; keys?: Record<string, string> }
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false

  await api('/push/subscribe', {
    method: 'POST',
    body: { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth },
  })

  return true
}

export async function disablePush(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration('/')
  const subscription = await registration?.pushManager.getSubscription()
  if (!subscription) return

  // Server first: if the local unsubscribe succeeded and the server call then
  // failed, the row would live on and she would keep getting notifications
  // with no way left to turn them off.
  await api('/push/unsubscribe', { method: 'POST', body: { endpoint: subscription.endpoint } })
  await subscription.unsubscribe()
}

export async function sendTestPush(): Promise<{ sent: number; gone: number; failed: number }> {
  return api('/push/test', { method: 'POST' })
}
